import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import './DonorLanding.css';
import { trackEvent } from "../../utils/analytics";


function DonorLanding() {
  const navigate = useNavigate();
  const { currentUser, userRole, loading } = useAuth();
  const [featuredNeeds, setFeaturedNeeds] = useState([]);
  const [needLoading, setNeedLoading] = useState(true);

  useEffect(() => {
    if (!loading) {
      if (currentUser && userRole === 'donor') {
        navigate('/give/dashboard');
        return;
      }
      if (currentUser && userRole === 'org') {
        navigate('/org/dashboard');
        return;
      }
      if (currentUser && userRole === 'admin') {
        navigate('/admin/dashboard');
        return;
      }
    }
  }, [currentUser, userRole, loading, navigate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchFeaturedNeeds();
  }, []);

  async function fetchFeaturedNeeds() {
    try {
      const q = query(
        collection(db, 'needs'),
        where('status', '==', 'open')
      );
      const snapshot = await getDocs(q);
      const needs = snapshot.docs
  .map(d => ({ id: d.id, ...d.data() }))
  .filter(n => 
    (n.pricePerCycle || 0) <= 30 &&
    n.isParent !== true &&
    (n.type || 'supply') === 'supply'
  );
      if (needs.length === 0) {
        setFeaturedNeeds([]);
        return;
      }

function shuffled(arr) {
  return [...arr].sort(() => Math.random() - 0.5);
}

const critical = shuffled(
  needs.filter(n => n.priority === 'critical')
);
const core = shuffled(
  needs.filter(n => n.priority === 'core')
);
const supplemental = shuffled(
  needs.filter(n => n.priority === 'supplemental')
);

const featured = [];
critical.slice(0, 2).forEach(n => featured.push(n));
const coreSlots = Math.min(core.length, 3 - featured.length);
core.slice(0, coreSlots).forEach(n => featured.push(n));
const suppSlots = Math.min(supplemental.length, 3 - featured.length);
supplemental.slice(0, suppSlots).forEach(n => featured.push(n));

setFeaturedNeeds(featured);

    } catch (err) {
      console.error('Error fetching featured needs:', err);
    } finally {
      setNeedLoading(false);
    }
  }

  if (loading) return null;

  return (
    <div className="donor-landing">

      <header className="landing-header">
        <div className="logo">somebody.gives</div>
        <div className="hero-location">📍 Springfield, MO</div>
        <div className="header-links">
  <button onClick={() => navigate('/give/causes')} className="btn-primary">
    Browse Causes
  </button>

  <button onClick={() => navigate('/impact')} className="btn-secondary">
    Community Impact
  </button>

  <button onClick={() => navigate('/give/login')} className="btn-secondary">
    Sign In
  </button>
</div>


      </header>

<section className="responsibility-section">
  <div className="responsibility-content">
    <p className="responsibility-text">
      Everybody knew the need.<br />
      Anybody could have helped.<br />
      Nobody was responsible.<br />
      So nothing changed.
    </p>
    <p className="responsibility-pivot">
      Until somebody made it theirs.
    </p>
    <p className="responsibility-close">
       
    </p>
  </div>
</section>


      <section className="hero">
  
  <h1>Somebody Gives.<br />Be that Somebody.</h1>
  
        <p className="hero-sub">
  Pick a real recurring need at a local nonprofit.
They receive it on your behalf every month. Automatically.
No reminders. No re-asking.
</p>
<div className="hero-trust">
  <span>✓ Real needs from vetted Springfield nonprofits</span>
  <span>✓ Delivered directly to the nonprofit every month — guaranteed</span>
  <span>✓ Stop anytime</span>
</div>

<div className="cause-path-cta">
  <div>
    <div className="cause-path-kicker">Not sure where to start?</div>
    <h2>Start with a cause you care about.</h2>
    <p>
      Explore local needs by issue area — food, housing, education,
      child life, community health, and more.
    </p>
  </div>

  <button
    onClick={() => {
      trackEvent("click_browse_causes", { source: "donor_landing_hero" });
      navigate('/give/causes');
    }}
    className="btn-primary btn-large"
  >
    Browse causes →
  </button>
</div>

        {needLoading && (
          <div className="start-here">
            <div className="start-here-card">
              <div className="start-here-item">
                <p style={{ color: '#aaa' }}>
                  Loading current needs...
                </p>
              </div>
            </div>
          </div>
        )}
{!needLoading && featuredNeeds.length === 0 && (
  <div className="start-here">
    <div className="start-here-card">
      <div className="start-here-item">
        <h2>Springfield nonprofits need you</h2>
        <p>
          Sign up to browse open needs from vetted local
          organizations and take responsibility for one.
        </p>
      </div>
      <button
        onClick={() => {
        trackEvent("signup_started", {
          source: "donor_landing_featured_empty"
        });
        navigate('/give/signup');
      }}
        className="btn-primary btn-large"
      >
        Get started →
      </button>
    </div>
  </div>
)}
        

        {!needLoading && featuredNeeds.length > 0 && (
          <div className="start-here">
            <div className="start-here-label">
              
            </div>
            <div className={`start-here-cards${
              featuredNeeds.length > 1 ? ' multi' : ''
            }`}>
              {featuredNeeds.map(need => (
                <div key={need.id} className="start-here-card">
                  <div className="start-here-item">
                    <span className={`priority-badge ${need.priority}`}>
                      {need.priority}
                    </span>
                    <h2>{need.itemName}</h2>
                    <p className="start-here-org"
                    style={{ cursor: 'pointer', textDecoration: 'underline',
                      opacity: 0.9 }}
                    onClick={() => navigate(`/o/${need.orgId}`)}>
                    {need.orgName}
                  </p>
                    <p>{need.impactStatement}</p>
                    <div className="start-here-cost">
  ${Math.ceil(need.pricePerCycle)}
  <span> / {need.frequency}</span>
</div>
{need.sponsorCount > 0 && (
  <div className="start-here-coverage">
    {need.sponsorCount} of {need.totalUnits || 1} sponsored
  </div>
)}
                  </div>

<button
  onClick={() => {
  trackEvent('click_take_responsibility', {
    need_id: need.id,
    item_name: need.itemName,
    org_name: need.orgName,
    price: need.pricePerCycle
  });
  navigate(`/give/signup?need=${need.id}&item=${encodeURIComponent(need.itemName)}&org=${encodeURIComponent(need.orgName)}`);
}}

  className="btn-primary btn-large"
>
  Take responsibility →
</button>

<div className="start-here-cancel">
  Cancel anytime. No commitment beyond monthly.
</div>


                </div>
              ))}
            </div>
            
            <div className="start-here-note">
  <span
  style={{ cursor: 'pointer', textDecoration: 'underline' }}
  onClick={() => {
    trackEvent("click_browse_causes", { source: "donor_landing_featured" });
    navigate('/give/causes');
  }}
>
  Browse causes
</span>

<span style={{ margin: '0 0.5rem', opacity: 0.45 }}>|</span>

<span
  style={{ cursor: 'pointer', textDecoration: 'underline' }}
  onClick={() => {
    trackEvent("click_browse_needs");
    navigate('/give/browse-public');
  }}
>
  Browse all open needs
</span>

<span style={{ margin: '0 0.5rem', opacity: 0.45 }}>|</span>

<span
  style={{ cursor: 'pointer', textDecoration: 'underline' }}
  onClick={() => {
    trackEvent("click_browse_sponsorships", { source: "donor_landing_featured" });
    navigate('/give/browse-sponsorships');
  }}
>
  Browse sponsorships
</span>
  </div>


          </div>
        )}

      </section>


      <section className="how-it-works">
        <h2>How it works</h2>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <h3>Choose a need</h3>
            <p>
              Browse real, specific needs from vetted
              Springfield nonprofits
            </p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h3>Take responsibility</h3>
            <p>
              Adopt that need. Your card is charged
              automatically on schedule.
            </p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h3>It shows up</h3>
            <p>
              The item ships directly to the nonprofit.
              You get confirmation every time.
            </p>
          </div>
          <div className="step">
            <div className="step-number">4</div>
            <h3>Feel the impact</h3>
            <p>
              Quarterly reports show exactly what your
              commitment made possible.
            </p>
          </div>
        </div>
      </section>





      <section className="impact-section">
        <blockquote>
          "I'm the reason that shelter has laundry detergent.
          I've been doing it for 14 months.
          192 loads of laundry have happened because of me."
        </blockquote>
      </section>

      <section className="browse-cta">
        <h2>Ready to take responsibility for a need?</h2>
        <p>
          All needs are from vetted Springfield nonprofits.
          Your support stays local.
        </p>
        <button
          onClick={() => {
  trackEvent("signup_started", {
    source: "donor_landing_bottom_cta"
  });
  navigate('/give/signup');
}}
          className="btn-primary btn-large"
        >
          Get started →
        </button>
        <div className="signin-link">
          Already have an account?{' '}
          <span onClick={() => navigate('/give/login')}>
            Sign in
          </span>
        </div>
  <div className="signin-link">
  Nonprofit accounts: <span
  onClick={() => {
    trackEvent("click_nonprofit_access", {
      source: "donor_landing_footer_cta"
    });
    navigate('/org');
  }}
  style={{ cursor: 'pointer', textDecoration: 'underline' }}
>
  Access platform here
</span>
</div>
  
      </section>

      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-logo">somebody.gives</div>
          <div className="footer-tagline">
          We turn unpredictable donations into guaranteed supply.
          </div>
<div className="footer-community">
  Currently serving Springfield, MO — more cities coming soon.
</div>
          <div className="footer-links">
  <a href="/org">For nonprofits — apply to list your needs</a>
  <a href="/admin">Admin</a>
</div>

        </div>
      </footer>

    </div>
  );
}

export default DonorLanding;